import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Img, { ImgProps } from "@insite/mobius/Img";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as cssLinter from "css";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const enum fields {
    imageUrl = "imageUrl",
    altText = "altText",
    imageLink = "imageLink",
    customCSS = "customCSS",
    imageSize = "imageSize",
    imageAlign = "imageAlign",
    imageCenter = "imageCenter",
    imageToSides = "imageToSides",
    imageResponsive = "imageResponsive"
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.imageUrl]: string;
        [fields.altText]: string;
        [fields.imageLink]: LinkFieldValue;
        [fields.customCSS]: string;
        [fields.imageSize]: string;
        [fields.imageAlign]: string;
        [fields.imageCenter]:boolean;
        [fields.imageToSides]: boolean;
        [fields.imageResponsive]:boolean;
    };
    extendedStyles?: ImageStyles;
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    const link = getLink(state, ownProps.fields.imageLink);
    return { pageUrl: link?.url };
};



export interface ImageStyles {
    wrapper?: InjectableCss;
    image?: LazyImageProps;
    img?: ImgProps;
    imageLink?: LinkPresentationProps;
}

export const imageStyles: ImageStyles = {
    image: {
        css: css`
            max-width: 100%;
        `,
    },
    
};

const styles = imageStyles;

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

const Image: React.FunctionComponent<Props> = ({ fields, pageUrl, extendedStyles }: Props) => {
    const alignmentStyle = {
        css:css`
            float: ${fields.imageAlign};
        `,
    };

    const centerImage = {
        css:css`
            display:block;
            margin-left:auto;
            margin-right: auto;
            width: 40%;
        `,
    };

    const responsiveImage = {
        css:css`
            width: 100%;
        `
    }

    let imageStylesAlign;
    if(fields.imageCenter){
        imageStylesAlign = centerImage;
    }
    else if(fields.imageToSides){
        imageStylesAlign = alignmentStyle;
    }
    else if(fields.imageResponsive){
        imageStylesAlign = responsiveImage;
    }



    const image = <LazyImage className="img" src={fields.imageUrl} altText={fields.altText} {...styles.image}  {...imageStylesAlign} />;

    const customCssWrapper = {
        css: css`
            ${fields.customCSS}
        `,
    };

    return (
    <StyledWrapper {...customCssWrapper}>
        <StyledWrapper className="img-wrapper" {...styles.wrapper}>
            {pageUrl ? (
                <Link className="img-link" href={pageUrl} {...styles.imageLink}>
                    {image}
                </Link>
            ) : (
                image
            )}
        </StyledWrapper>
    </StyledWrapper>
    );
};

const defaultCustomCss = `.img-wrapper {
}

.img-link {
}

.img {
}
`;

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};


const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(Image),
    definition: {
        group: "Basic",
        icon: "Image",
        fieldDefinitions: [
            {
                name: fields.imageUrl,
                fieldType: "Contextual",
                editorTemplate: "ImagePickerField",
                tooltip: "Image has to be 364 x 243",
                defaultValue: "",
                tab: basicTab,
            },
            {
                name: fields.altText,
                fieldType: "Contextual",
                editorTemplate: "TextField",
                defaultValue: "",
                tab: basicTab,
            },
            {
                name: fields.imageLink,
                fieldType: "Contextual",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                tab: basicTab,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "Contextual",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });

                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
            {
                fieldType: "General",
                name: fields.imageCenter,
                editorTemplate:"CheckboxField",
                displayName: "Center Image",
                defaultValue: false,
            },
            {
                fieldType: "General",
                name: fields.imageResponsive,
                editorTemplate:"CheckboxField",
                displayName: "Set Responsive Style",
                defaultValue: false,
            },
            {
                fieldType: "General",
                name: fields.imageToSides,
                editorTemplate:"CheckboxField",
                displayName: "Set Align to Image",
                defaultValue: false,
            },
            {
                fieldType: "Translatable",
                name: fields.imageAlign,
                displayName: "Image Alignment",
                editorTemplate: "DropDownField",
                options:[
                    {displayName: "Left", value: "left"},
                    {displayName: "Right", value: "right"},
                ],
                defaultValue: "",
                hideEmptyOption: true,
                isVisible: widget => widget.fields.imageToSides,
            },
            {
                fieldType: "General",
                name: fields.imageSize,
                editorTemplate: "TextField",
                displayName: "Recommended Image size",
                defaultValue: "364 x 243",
            },
        ],
    },
};

export default widgetModule;
